import { Utils } from 'ng/src/app/core/utils';
import { TemplateSegment } from 'ng/src/app/templates/shared/template.model';
import { Template, TemplateTypeId } from '../../shared/template.model';

export interface ContainerEntry {
	idx: number,
	originIdx: number,
	segment?: TemplateSegment,
	parent?: ContainerEntry,
	entries: ContainerEntry[],
	
	// The container this Entry belongs to
	container: ITemplateContainer,
}

export type ReadonlyContainerEntry = Readonly<Omit<ContainerEntry, 'entries' | 'parent'>> & {
	parent?: ReadonlyContainerEntry,
	entries: ReadonlyArray<ReadonlyContainerEntry>
}

export type TemplateContainerCreationResult = {
	entries: ReadonlyArray<ReadonlyContainerEntry>,
	map: Readonly<Record<number, ReadonlyContainerEntry>>
}

interface ITemplateContainer {
	readonly templateId: number;
	readonly templateTypeId: TemplateTypeId;
	readonly templateName: string;
	
	readonly entries: ReadonlyArray<ReadonlyContainerEntry>;
	readonly entryMap: Readonly<Record<number, ReadonlyContainerEntry>>;
	
	flatten(): ReadonlyArray<ReadonlyContainerEntry>;
}

export class TemplateContainerView implements ITemplateContainer {
	readonly templateId: number;
	readonly templateTypeId: TemplateTypeId;
	readonly templateName: string;
	
	readonly entries: ReadonlyArray<ReadonlyContainerEntry>;
	readonly entryMap: Readonly<Record<number, ReadonlyContainerEntry>>;
	
	private flattened?: ReadonlyArray<ReadonlyContainerEntry>;
	
	constructor(
		protected readonly template: Pick<Template, 'id' | 'templateType' | 'name'>,
		result: TemplateContainerCreationResult,
	) {
		this.templateId = template.id;
		this.templateTypeId = template.templateType.id;
		this.templateName = template.name;
		
		this.entries = result.entries;
		this.entryMap = result.map;
	}
	
	flatten(): ReadonlyArray<ReadonlyContainerEntry> {
		if (this.flattened) return this.flattened;
		
		const result = createFlattenedEntries(this.entries as ReadonlyContainerEntry[]);
		
		return this.flattened = Object.freeze(result);
	}
}

export abstract class TemplateContainer implements ITemplateContainer, Iterable<ReadonlyContainerEntry> {
	readonly templateId: number;
	readonly templateTypeId: TemplateTypeId;
	readonly templateName: string;
	
	readonly entries: ReadonlyArray<ReadonlyContainerEntry>;
	readonly entryMap: Readonly<Record<number, ReadonlyContainerEntry>>;
	private flattened?: ReadonlyArray<ReadonlyContainerEntry>;
	
	private _nonObsoleteView?: TemplateContainerView;
	private _hasHiddenObsoleteEntries?: boolean;
	
	public constructor(
		protected template: Template,
		segments: TemplateSegment[]
	) {
		const result = this.create(template, segments);
		this.entries = result.entries;
		this.entryMap = result.map;
		
		this.templateId = template.id;
		this.templateTypeId = template.templateType.id;
		this.templateName = template.name;
	}
	
	protected abstract create(template: Template, segments: TemplateSegment[]): TemplateContainerCreationResult;
	
	protected createResult(entries: TemplateContainerCreationResult['entries'] = [], map: TemplateContainerCreationResult['map'] = {}): TemplateContainerCreationResult {
		return {
			entries,
			map
		}
	}
	
	protected createEntry(opts: Omit<ContainerEntry, 'container'>): ContainerEntry {
		return Utils.defineGetters(opts, {
			container: {
				get: () => this,
			}
		})
	}
	
	abstract viewableInNonObsoleteView(entry: ReadonlyContainerEntry): boolean;
	
	//Creates a new view of the container for if you are wanting to show non-obsolete viewable entries.
	getNonObsoleteView(): TemplateContainerView {
		if (this._nonObsoleteView) {
			return this._nonObsoleteView;
		}
		
		const result = this.createNonObsoleteView();
		this._nonObsoleteView = result;
		
		return result;
	}
	
	flatten(): ReadonlyArray<ReadonlyContainerEntry> {
		if (this.flattened) return this.flattened;
		
		const result = createFlattenedEntries(this.entries as ReadonlyContainerEntry[]);
		
		return this.flattened = Object.freeze(result);
	}
	
	hasHiddenObsoleteEntries(): boolean {
		if (this._hasHiddenObsoleteEntries !== undefined) {
			return this._hasHiddenObsoleteEntries;
		}
		
		this._hasHiddenObsoleteEntries = -1 !== this.flatten().findIndex((e) => {
			return e.segment && e.segment.obsolete && !this.viewableInNonObsoleteView(e);
		});
		
		return this._hasHiddenObsoleteEntries;
	}
	
	get length() {
		return this.entries.length;
	}
	
	get(index: number): ReadonlyContainerEntry | undefined {
		return this.entries[index];
	}
	
	getById(id = -1): ReadonlyContainerEntry | undefined {
		return this.entryMap[id]
	}
	
	[Symbol.iterator](): Iterator<ReadonlyContainerEntry> {
		let counter = 0;
		return {
			next: () => {
				const value = this.entries[counter];
				counter += 1;
				return {
					value: value,
					done: counter > this.entries.length
				}
			}
		}
	}
	
	private createNonObsoleteView(): TemplateContainerView {
		const all = this.flatten().reduce((arr, e) => {
			if (!e.segment) {
				return arr;
			}
			
			if (this.viewableInNonObsoleteView(e)) {
				arr.push(e.segment)
			}
			
			return arr;
		}, [] as TemplateSegment[]);
		
		const result = this.create(this.template, all);
		return new TemplateContainerView(this.template, result);
	}
}

function createFlattenedEntries(entries: ReadonlyContainerEntry[]): ReadonlyArray<ReadonlyContainerEntry> {
	const flattened = entries.reduce((arr, e) => {
		arr.push(...flatten([e]));
		return arr;
	}, new Array<ReadonlyContainerEntry>());
	
	return Object.freeze(flattened);
	
	function flatten(ReadonlyContainerEntry: ReadonlyContainerEntry[]): ReadonlyContainerEntry[] {
		return ReadonlyContainerEntry.reduce((arr, e) => {
			arr.push(e);
			
			if (e.entries.length > 0) {
				arr.push(...flatten(Array.from(e.entries)));
			}
			
			return arr;
		}, [] as ReadonlyContainerEntry[]);
	}
}